package com.mgacreative.hotel.feature.reservation.ui.checkin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

import com.mgacreative.hotel.domain.repository.ReservationRepository
import com.mgacreative.hotel.domain.repository.RoomRepository
import com.mgacreative.hotel.domain.repository.PricingRepository
import com.mgacreative.hotel.domain.repository.BlacklistRepository
import com.mgacreative.hotel.domain.model.BlacklistedGuest

data class AvailableRoomUI(
    val id: String,
    val roomNumber: String,
    val roomType: String,
    val status: String,
    val currentPrice: Double
)

class CheckInViewModel(
    private val reservationRepository: ReservationRepository,
    private val roomRepository: RoomRepository,
    private val pricingRepository: PricingRepository,
    private val blacklistRepository: BlacklistRepository
) : ViewModel() {
    private val _isProcessing = MutableStateFlow(false)
    val isProcessing: StateFlow<Boolean> = _isProcessing.asStateFlow()

    private val _checkIns = MutableStateFlow<List<CheckInItemData>>(emptyList())
    val checkIns: StateFlow<List<CheckInItemData>> = _checkIns.asStateFlow()

    suspend fun checkBlacklist(fullName: String, identityNo: String): BlacklistedGuest? {
        return blacklistRepository.checkIsBlacklisted(fullName, identityNo)
    }

    fun loadTodayCheckIns() {
        viewModelScope.launch {
            try {
                val allReservations = reservationRepository.getReservations()
                // Durumu CONFIRMED olanları (veya CHECKED_IN olup odası atanmamışları) Check-In için göster
                val pendingList = allReservations.filter { 
                    it.status == "CONFIRMED" || (it.status == "CHECKED_IN" && it.roomId == null)
                }
                
                _checkIns.value = pendingList.map { res ->
                    val fName = res.guests?.firstName ?: ""
                    val lName = res.guests?.lastName ?: ""
                    CheckInItemData(
                        id = res.id ?: "",
                        guestName = if (fName.isNotBlank()) "$fName $lName" else "Guest",
                        firstName = fName,
                        lastName = lName,
                        identityNo = res.guests?.idPassportNo ?: "",
                        birthDate = res.guests?.birthDate ?: "",
                        rooms = listOf(res.rooms?.roomNumber ?: res.roomId ?: res.roomType ?: "Unknown"),
                        dateRange = "${res.checkInDate} - ${res.checkOutDate}",
                        checkInDate = res.checkInDate,
                        checkOutDate = res.checkOutDate,
                        statusText = "Pending",
                        isCheckInComplete = false,
                        roomType = res.roomType ?: "",
                        kbsStatus = res.kbsStatus ?: "BEKLIYOR",
                        kbsSentAt = res.kbsSentAt
                    )
                }
            } catch (e: Exception) {
                // Handle error
            }
        }
    }

    suspend fun getAvailableRooms(roomType: String): List<AvailableRoomUI> {
        return try {
            val allRooms = roomRepository.getRooms()
            val availableRooms = allRooms.filter { 
                it.status.equals("AVAILABLE", ignoreCase = true) || it.status.equals("CLEAN", ignoreCase = true) 
            }
            
            val prices = pricingRepository.getAllPricePeriods()
            val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()
            
            availableRooms.map { room ->
                val activePrice = prices.filter { 
                    it.roomType.equals(room.roomType, ignoreCase = true) && it.startDate <= today && it.endDate >= today 
                }.maxByOrNull { it.price }?.price ?: 0.0
                
                AvailableRoomUI(
                    id = room.id ?: "",
                    roomNumber = room.roomNumber,
                    roomType = room.roomType,
                    status = room.status,
                    currentPrice = activePrice
                )
            }.sortedWith(compareByDescending<AvailableRoomUI> { it.roomType.equals(roomType, ignoreCase = true) }
             .thenBy { it.roomNumber.toIntOrNull() ?: 0 })
        } catch (e: Exception) {
            emptyList()
        }
    }

    fun completeCheckIn(reservationId: String, roomId: String) {
        viewModelScope.launch {
            _isProcessing.value = true
            try {
                // 1. Odanın durumunu OCCUPIED yap
                roomRepository.updateRoomStatus(roomId, "OCCUPIED")
                
                // 2. Rezervasyona odayı ata ve status'ü CHECKED_IN yap
                reservationRepository.updateReservationStatusAndRoom(reservationId, "CHECKED_IN", roomId)
                
                // Yeniden yükle
                loadTodayCheckIns()
            } catch (e: Exception) {
                e.printStackTrace()
            } finally {
                _isProcessing.value = false
            }
        }
    }

    fun sendKbsForReservation(reservationId: String, onComplete: ((Boolean) -> Unit)? = null) {
        viewModelScope.launch {
            try {
                val nowIso = Clock.System.now().toString()
                reservationRepository.updateKbsStatus(reservationId, "BILDIRILDI", nowIso)
                _checkIns.value = _checkIns.value.map { item ->
                    if (item.id == reservationId) {
                        item.copy(kbsStatus = "BILDIRILDI", kbsSentAt = nowIso)
                    } else item
                }
                onComplete?.invoke(true)
            } catch (e: Exception) {
                e.printStackTrace()
                onComplete?.invoke(false)
            }
        }
    }

    fun sendAllPendingKbs(onResult: (Int, Int) -> Unit) {
        viewModelScope.launch {
            try {
                val pending = _checkIns.value.filter { it.kbsStatus != "BILDIRILDI" }
                val nowIso = Clock.System.now().toString()
                var successCount = 0
                for (item in pending) {
                    try {
                        reservationRepository.updateKbsStatus(item.id, "BILDIRILDI", nowIso)
                        successCount++
                    } catch (e: Exception) {
                        e.printStackTrace()
                    }
                }
                _checkIns.value = _checkIns.value.map { item ->
                    if (item.kbsStatus != "BILDIRILDI") {
                        item.copy(kbsStatus = "BILDIRILDI", kbsSentAt = nowIso)
                    } else item
                }
                onResult(successCount, pending.size)
            } catch (e: Exception) {
                onResult(0, 0)
            }
        }
    }
}

// UI State Model
data class CheckInItemData(
    val id: String,
    val guestName: String,
    val firstName: String = "",
    val lastName: String = "",
    val identityNo: String = "",
    val birthDate: String = "",
    val rooms: List<String>,
    val dateRange: String,
    val checkInDate: String = "",
    val checkOutDate: String = "",
    val statusText: String,
    val isCheckInComplete: Boolean,
    val roomType: String,
    val kbsStatus: String = "BEKLIYOR",
    val kbsSentAt: String? = null
)
